import { Add } from "@carbon/icons-react";
import { type RowData, type Table } from "@tanstack/react-table";
import cx from "classnames";

import { Button } from "../../Button";
import { emptyRow } from "../useColumnState";
import { GroupedRowsColumnHeader } from "./GroupedRowsColumnHeader";
import { RenderedCell } from "./RenderedCell";
import { type TableGroup, getRowGroup } from "./TableGroup";
import { rowShouldUseCursorPointer } from "./rowShouldUseCursorPointer";
import { type RowSelectBehavior } from "./types";

export type Props<T extends RowData> = {
	table: Table<T>;
	tableGroup?: TableGroup;
	rowSelectBehavior?: RowSelectBehavior;
	/**
	 * Specifies which events cause a row to be selected, e.g., clicking the entire row, or just a
	 * toggle such as a radio button.
	 */
	rowSelectTrigger?: "onClickRow" | "onClickToggle";
};

export function GroupedRows<T extends RowData>({
	table,
	tableGroup,
	rowSelectBehavior,
	rowSelectTrigger,
}: Props<T>) {
	const { meta } = table.options;
	const rows = table.getRowModel().rows;
	return (
		<div className="flex flex-col gap-2 divide-y rounded-b-md">
			{rows.flatMap((row, i) => {
				const exemplarGroupedCell = getRowGroup(row.subRows[0]!, tableGroup)[0]!;

				return (
					<div className="group/aggregate flex">
						<div
							className={cx("flex flex-col mr-px bg-secondary", {
								"rounded-bl-md": i === rows.length - 1,
							})}
						>
							<RenderedCell
								key={exemplarGroupedCell.id}
								cell={exemplarGroupedCell}
								groupIndices={row.subRows.map((subRow) => subRow.index)}
							/>
							<Button
								className={cx("w-full mt-auto !rounded-none", {
									"!rounded-bl-md": i === rows.length - 1,
								})}
								level="tertiary"
								size="sm"
								onClick={() => {
									meta?.insertRow?.(
										row.index + 1,
										Object.assign({}, emptyRow(table as Table<unknown>) as T, {
											[exemplarGroupedCell.column.id]:
												exemplarGroupedCell.getValue(),
										})
									);
								}}
							>
								<div className="flex items-center gap-1 w-full">
									<Add />
									<div className="invisible focus:visible group-hover/aggregate:visible text-tertiary">
										Add row
									</div>
								</div>
							</Button>
						</div>
						<div className="w-full bg-primary">
							{row.subRows.map((subRow) => (
								<div
									key={subRow.id}
									className={cx(
										"flex",
										"flex-row",
										"[&>:first-child]:pl-3",
										"shadow-border-bottom",
										"relative",
										"group",
										{
											"cursor-pointer": rowShouldUseCursorPointer(
												rowSelectBehavior,
												rowSelectTrigger
											),
											"!bg-brand-translucent": subRow.getIsSelected(),
										}
									)}
								>
									{getRowGroup(subRow, tableGroup)
										.slice(1)
										.map((cell) => (
											<RenderedCell key={cell.id} cell={cell} />
										))}
								</div>
							))}
							<div
								key={row.id}
								className={cx(
									"overflow-hidden",
									"flex",
									"flex-row",
									"[&>:first-child]:pl-3"
								)}
							>
								{getRowGroup(row, tableGroup)
									.slice(1)
									.map((cell) => (
										<GroupedRowsColumnHeader
											key={cell.id}
											table={table}
											cell={cell}
										/>
									))}
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}
